import type { HistoryEntry, Plan, PlanSession, Task } from '$lib/domain/types';
import type { AIInsightCard } from '$lib/domain/blueprint';

export type FocusDocument = {
  schemaVersion: 1;
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  tasks: Task[];
  planBlocks: Plan[];
  sessions: PlanSession[];
  history: HistoryEntry[];
  aiInsights: AIInsightCard[];
};

export type SaveProgress = {
  phase: 'serializing' | 'writing' | 'complete' | 'error';
  progress: number;
};

const documentKey = (id: string) => `focus-document:${id}`;

export function createInitialDocument(id = 'default'): FocusDocument {
  const now = new Date().toISOString();
  return {
    schemaVersion: 1,
    id,
    title: 'Focus Flow',
    createdAt: now,
    updatedAt: now,
    tasks: [],
    planBlocks: [],
    sessions: [],
    history: [],
    aiInsights: []
  };
}

export function createFocusDocument(input: {
  id?: string;
  title?: string;
  tasks: Task[];
  planBlocks: Plan[];
  sessions: PlanSession[];
  history: HistoryEntry[];
  aiInsights?: AIInsightCard[];
  createdAt?: string;
}): FocusDocument {
  const now = new Date().toISOString();

  return {
    schemaVersion: 1,
    id: input.id ?? 'default',
    title: input.title ?? 'Focus Flow',
    createdAt: input.createdAt ?? now,
    updatedAt: now,
    tasks: input.tasks,
    planBlocks: input.planBlocks,
    sessions: input.sessions,
    history: input.history,
    aiInsights: input.aiInsights ?? []
  };
}

export async function saveFocusDocument(
  doc: FocusDocument,
  onProgress?: (progress: SaveProgress) => void
): Promise<void> {
  try {
    onProgress?.({ phase: 'serializing', progress: 0.2 });
    await Promise.resolve();

    const payload = JSON.stringify({ ...doc, updatedAt: new Date().toISOString() }, null, 2);

    onProgress?.({ phase: 'writing', progress: 0.7 });
    localStorage.setItem(documentKey(doc.id), payload);
    onProgress?.({ phase: 'complete', progress: 1 });
  } catch (error) {
    onProgress?.({ phase: 'error', progress: 1 });
    throw error;
  }
}

export function loadFocusDocument(id: string): FocusDocument | null {
  const raw = localStorage.getItem(documentKey(id));
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<FocusDocument>;
    if (parsed.schemaVersion !== 1 || !parsed.id) return null;
    return {
      ...createInitialDocument(parsed.id),
      ...parsed,
      tasks: Array.isArray(parsed.tasks) ? parsed.tasks : [],
      planBlocks: Array.isArray(parsed.planBlocks) ? parsed.planBlocks : [],
      sessions: Array.isArray(parsed.sessions) ? parsed.sessions : [],
      history: Array.isArray(parsed.history) ? parsed.history : [],
      aiInsights: Array.isArray(parsed.aiInsights) ? parsed.aiInsights : []
    };
  } catch {
    return null;
  }
}
